import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Sheet, SheetContent, SheetTitle } from '@artstorefronts/ui';
import { ArrowLeft, FrownIcon, History, SquarePen } from 'lucide-react';

import { ChatUIProvider } from '~/context/chatUI';
import ChatInput from '~/components/Chat/ChatInput';
import { useFileUpload } from '~/components/Chat/hooks/useFileUpload';
import { completedFilesToFileParts } from '~/components/Chat/utils/fileParts';
import MessageList from '~/components/Chat/MessageList';
import { LoadEarlierMessages, usePrependScroll } from '~/components/Chat/LoadEarlierMessages';
import { ArtyRenderContext } from '~/components/Chat/artyRenderContext';
import { scrollToBottom } from '~/components/Chat/utils';
import { ArtyActionsProvider } from '~/components/arty/card';

import { useArtyFlyoutView } from './ArtyFlyoutProvider';
import { FlyoutHistory } from './FlyoutHistory';

const CARD_RENDER = { card: true };

// Same as /chat: a failed turn's partial reply goes before the next turn is sent.
function dropFailedReply(chat: any) {
  const lastUserIndex = chat.messages.findLastIndex((message: any) => message.role === 'user');
  if (lastUserIndex === -1) {
    return;
  }

  chat.setMessages(chat.messages.slice(0, lastUserIndex + 1));
}

function isVisibleError(error: any) {
  if (!error) {
    return false;
  }

  return !error.message?.includes('401') && !error.message?.includes('Unauthorized');
}

function FlyoutError({ error }: { error: any }) {
  const { t } = useTranslation('chat');

  if (!isVisibleError(error)) {
    return null;
  }

  return (
    <div className="my-4 flex items-center gap-3 rounded-lg border border-gray-300 bg-gray-50 p-4 text-sm">
      <FrownIcon className="size-5 shrink-0" />
      <p>{t('error-message')}</p>
    </div>
  );
}

function FlyoutHeader() {
  const { t } = useTranslation('chat');
  const { chat, newChat, panel, showHistory, showChat } = useArtyFlyoutView();

  if (panel === 'history') {
    return (
      <header className="flex shrink-0 items-center gap-2 border-b border-gray-200 py-2 pl-2 pr-12">
        <Button
          variant="ghost"
          size="sm"
          onClick={showChat}
          aria-label={t('panel.back-to-chat')}
          title={t('panel.back-to-chat')}
        >
          <ArrowLeft className="size-4" />
        </Button>
        <SheetTitle className="min-w-0 flex-1 truncate text-sm font-medium">
          {t('controls.conversation-history')}
        </SheetTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={newChat}
          aria-label={t('controls.new-conversation')}
          title={t('controls.new-conversation')}
        >
          <SquarePen className="size-4" />
        </Button>
      </header>
    );
  }

  return (
    <header className="flex shrink-0 items-center gap-2 border-b border-gray-200 py-2 pl-4 pr-12">
      <SheetTitle className="min-w-0 flex-1 truncate text-sm font-medium">
        {chat.chatTitle || t('panel.region-label')}
      </SheetTitle>
      <Button
        variant="ghost"
        size="sm"
        onClick={showHistory}
        aria-label={t('controls.conversation-history')}
        title={t('controls.conversation-history')}
      >
        <History className="size-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={newChat}
        aria-label={t('controls.new-conversation')}
        title={t('controls.new-conversation')}
      >
        <SquarePen className="size-4" />
      </Button>
    </header>
  );
}

function FlyoutTranscript() {
  const { chat, cardTurn } = useArtyFlyoutView();
  const containerRef = useRef<HTMLDivElement>(null);
  const prependScroll = usePrependScroll(containerRef, chat.messages);

  useEffect(() => {
    if (prependScroll.restored()) return;
    scrollToBottom(containerRef.current);
  }, [chat.messages.length, cardTurn]);

  // Back from history remounts the transcript; land on the latest message once it has laid out.
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      scrollToBottom(containerRef.current, true);
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <div ref={containerRef} className="min-h-0 flex-1 overflow-y-auto">
      <div className="px-4 py-5">
        <LoadEarlierMessages chat={chat} onBeforeLoad={prependScroll.capture} onNothingLoaded={prependScroll.release} />
        <MessageList
          messages={chat.messages}
          status={chat.status}
          conversationId={chat.conversationId}
          onUploadComplete={chat.handleAppend}
          chatData={chat.chatData}
        />
        <FlyoutError error={chat.error} />
      </div>
    </div>
  );
}

function FlyoutComposer({ upload }: { upload: ReturnType<typeof useFileUpload> }) {
  const { chat, isSignedIn } = useArtyFlyoutView();
  const { selectedFiles, handleFilesSelected, removeFile, clearFiles } = upload;

  async function submit(value: string) {
    if (chat.error) {
      dropFailedReply(chat);
    }

    chat.setInput('');
    try {
      const completed = selectedFiles.filter((file) => file.status === 'completed');
      const sent = await chat.sendText(value, undefined, completedFilesToFileParts(completed));
      if (!sent) {
        chat.setInput(value);
      } else {
        clearFiles();
      }
    } catch (error) {
      console.error('Failed to send Arty message:', error);
      chat.setInput(value);
    }
  }

  return (
    <div className="shrink-0 pt-3">
      <ChatInput
        initialInput={chat.input}
        onInputSubmit={submit}
        onInputUpdate={chat.setInput}
        status={chat.status}
        selectedFiles={selectedFiles}
        onFilesSelected={handleFilesSelected}
        onRemoveFile={removeFile}
        error={chat.error}
        showSuggestions={false}
        conversationId={chat.draftChatId}
        key={chat.draftChatId}
        disabled={!isSignedIn || chat.awaitingWindow}
      />
    </div>
  );
}

export function ArtyFlyout() {
  const { chat, isOpen, close, isSignedIn, sendText, panel } = useArtyFlyoutView();
  const upload = useFileUpload();
  // Attachments belong to the chat they were added in; keyed ChatInput drops in-flight ones.
  useEffect(() => {
    upload.clearFiles();
  }, [chat.draftChatId]);
  const actions = useMemo(() => {
    return { isBusy: chat.isLoading || chat.awaitingWindow, isSignedIn, sendText };
  }, [chat.isLoading, chat.awaitingWindow, isSignedIn, sendText]);

  function onOpenChange(next: boolean) {
    if (next) {
      return;
    }

    close();
  }

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        aria-describedby={undefined}
        className="flex w-full max-w-none flex-col gap-0 p-0 sm:max-w-none md:w-[35vw] md:min-w-[20rem] md:max-w-[36rem]"
      >
        <ChatUIProvider config={{ showImageUpload: isSignedIn }}>
          <FlyoutHeader />
          {panel === 'history' ? (
            <FlyoutHistory />
          ) : (
            <>
              <ArtyActionsProvider chat={actions}>
                <ArtyRenderContext.Provider value={CARD_RENDER}>
                  <FlyoutTranscript />
                </ArtyRenderContext.Provider>
              </ArtyActionsProvider>
              <FlyoutComposer upload={upload} />
            </>
          )}
        </ChatUIProvider>
      </SheetContent>
    </Sheet>
  );
}
